import { useUpdateDeal } from "api/deals/service";
import { useGetChannels } from "api/channels/service";
import { useEffect, useImperativeHandle, useState, forwardRef, useMemo } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { getActiveColorStyle } from "utils/colorUtils";
import {
  getTheme2FormFieldsCss,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import {
    Row,
    Col,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    FormText,
} from "reactstrap";

import ChannelsEmbedded from "../../settings/Channels/ChannelsEmbedded";
import Select from "components/Select";
import {
    SALES_GROUP_PERSON_PAGE_SIZE,
    getSalesPersonOptions,
    getSalesManagerOptions,
    getFiPersonOptions,
    findSalesGroupPersonName,
} from "utils/salesGroupPersonOptions";
const DealDetailTab = forwardRef(({ data, activeColor = "blue", isTheme2 = false }, ref) => {
    const { t } = useTranslation();
    const updateDealMutation = useUpdateDeal();
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const selectStyles = useMemo(
        () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
        [activeColorStyle, isTheme2]
    );
    const theme2FieldsCss = useMemo(
        () =>
            isTheme2
                ? getTheme2FormFieldsCss({
                    scopeSelector: ".deal-detail-tab.deal-detail-tab--theme2",
                    fieldsSelector: ".deal-detail-tab-fields",
                })
                : "",
        [isTheme2]
    );
    const inputStyle = isTheme2 ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } : undefined;


    const [dealData, setDealData] = useState({
        application_type: "Individual Application",
        deal_origination: "Unknown",
        customer_hear_about: "Unknown",
        referral_source: "",
        desiredVehicle: "",
        sales_person_1: "",
        sales_person_1_commission: "0",
        sales_person_2: "",
        sales_person_2_commission: "0",
        sales_manager: "",
        fi_person: "",
        closing_probability: "50",
    });

    const { data: salesGroupPersons } = useGetChannels("sales_group_person", 1, SALES_GROUP_PERSON_PAGE_SIZE);
    const { data: referralSources } = useGetChannels("lead_referral_source", 1, SALES_GROUP_PERSON_PAGE_SIZE);

    const salesGroupPersonResults = salesGroupPersons?.results || [];

    const salesRoleOptions = useMemo(() => getSalesPersonOptions(salesGroupPersonResults), [salesGroupPersonResults]);
    const managerRoleOptions = useMemo(() => getSalesManagerOptions(salesGroupPersonResults), [salesGroupPersonResults]);
    const fiRoleOptions = useMemo(() => getFiPersonOptions(salesGroupPersonResults), [salesGroupPersonResults]);
    const referralSourceOptions = useMemo(() => {
        return referralSources?.results || [];
    }, [referralSources]);
    useEffect(() => {
        if (data) {
            setDealData({
                application_type: data.application_type,
                deal_origination: data.deal_origination,
                customer_hear_about: data.customer_hear_about,
                referral_source: data.referral_source?.id ? String(data.referral_source.id) : "",
                sales_person_1: data.sales_person_1?.id ? String(data.sales_person_1.id) : "",
                sales_person_1_commission: data.sales_person_1_commission,
                sales_person_2: data.sales_person_2?.id ? String(data.sales_person_2.id) : "",
                sales_person_2_commission: data.sales_person_2_commission,
                sales_manager: data.sales_manager?.id ? String(data.sales_manager.id) : "",
                fi_person: data.fi_person?.id ? String(data.fi_person.id) : "",
                closing_probability: data.closing_probability,
                desiredVehicle: data.desiredVehicle || "",
            });
        }
    }, [data]);

    const [focusedField, setFocusedField] = useState(null);
    const [showAddReferralSource, setShowAddReferralSource] = useState(false);

    const handleDealInputChange = (e) => {
        const { name, value } = e.target;

        // Commission ve closing probability alanları için özel kontrol
        if (name === 'sales_person_1_commission' || name === 'sales_person_2_commission' || name === 'closing_probability') {
            // % işaretini kaldır
            let cleanValue = value.replace('%', '');
            let numValue = parseFloat(cleanValue);

            // 100'den büyük değerleri engelle
            if (numValue > 100) {
                numValue = 100;
            }

            // Negatif değerleri engelle
            if (numValue < 0) {
                numValue = 0;
            }

            setDealData(prev => ({
                ...prev,
                [name]: numValue.toString()
            }));
        } else {
            setDealData(prev => {
                const newData = {
                    ...prev,
                    [name]: value
                };

                // CustomerHearAbout "Unknown" seçilirse referralSource'u temizle
                if (name === 'customer_hear_about' && value === 'Unknown') {
                    newData.referral_source = '';
                }

                return newData;
            });
        }
    };

    const handleFocus = (fieldName) => {
        setFocusedField(fieldName);
    };

    const handleBlur = () => {
        setFocusedField(null);
    };

    const handleReferralSourceChange = (value) => {
        if (value === "add_new_referral_source") {
            setShowAddReferralSource(true);
        } else {
            handleDealInputChange({ target: { name: 'referral_source', value: value } });
        }
    };
    const handleSave = async () => {

        const updateData = {
            detail: {
                application_type: dealData.application_type,
                deal_origination: dealData.deal_origination,
                customer_hear_about: dealData.customer_hear_about,
                referral_source: dealData.referral_source === "" ? null : Number(dealData.referral_source),
                desiredVehicle: dealData.desiredVehicle,
                sales_person_1: dealData.sales_person_1 === "" ? null : Number(dealData.sales_person_1),
                sales_person_1_commission: dealData.sales_person_1_commission,
                sales_person_2: dealData.sales_person_2 === "" ? null : Number(dealData.sales_person_2),
                sales_person_2_commission: dealData.sales_person_2_commission,
                sales_manager: dealData.sales_manager === "" ? null : Number(dealData.sales_manager),
                fi_person: dealData.fi_person === "" ? null : Number(dealData.fi_person),
                closing_probability: dealData.closing_probability,
            }
        }
        try {
            await updateDealMutation.mutateAsync({
                id: data.deal_id,
                data: updateData
            });
            return { success: true };
        } catch (error) {
            console.error("Error updating deal:", error);
        }
    }


    useImperativeHandle(ref, () => ({
        handleSave
    }));


    return (
        <div className={classNames("deal-detail-tab", { "deal-detail-tab--theme2": isTheme2 })}>
            {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
            {!showAddReferralSource && (
                <div className={classNames("row", { "deal-detail-tab-fields": isTheme2 })}>
                    <div className="col-md-6 mb-6">
                        <div className="card rounded-0">
                            <div className="card-header font-weight-bold">{t('dealDetailTab.dealDetail')}</div>

                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6">


                                        <FormGroup>
                                            <Label for="application_type">{t('dealDetailTab.applicationType')}</Label>
                                            <Select
                                                activeColor={activeColor}
                                                styles={selectStyles}
                                                className="react-select"
                                                placeholder={t('dealDetailTab.selectApplicationType')}
                                                name="application_type"
                                                isSearchable={false}
                                                closeMenuOnSelect={true}
                                                value={dealData.application_type ? {
                                                    value: dealData.application_type,
                                                    label: dealData.application_type === "Individual Application"
                                                        ? t('dealDetailTab.individualApplication')
                                                        : t('dealDetailTab.jointApplication')
                                                } : null}
                                                onChange={(option) => handleDealInputChange({ target: { name: 'application_type', value: option?.value || '' } })}
                                                options={[
                                                    { value: "Individual Application", label: t('dealDetailTab.individualApplication') },
                                                    { value: "Joint Application", label: t('dealDetailTab.jointApplication') }
                                                ]}
                                                isClearable
                                            />
                                        </FormGroup>
                                    </div>
                                    <div className="col-md-6">

                                        <FormGroup>
                                            <Label for="deal_origination">{t('dealDetailTab.dealOrigination')}</Label>
                                            <Select
                                                activeColor={activeColor}
                                                styles={selectStyles}
                                                className="react-select"
                                                placeholder={t('dealDetailTab.selectDealOrigination')}
                                                name="deal_origination"
                                                isSearchable={false}
                                                closeMenuOnSelect={true}
                                                value={dealData.deal_origination ? {
                                                    value: dealData.deal_origination,
                                                    label: dealData.deal_origination === "Unknown" ? t('dealDetailTab.unknown') :
                                                        dealData.deal_origination === "Walk-in" ? t('dealDetailTab.walkIn') :
                                                            dealData.deal_origination === "Phone" ? t('dealDetailTab.phone') :
                                                                dealData.deal_origination === "Internet" ? t('dealDetailTab.internet') :
                                                                    dealData.deal_origination === "Referral" ? t('dealDetailTab.referral') :
                                                                        dealData.deal_origination === "Previous Customer" ? t('dealDetailTab.previousCustomer') :
                                                                            dealData.deal_origination === "Trade-in" ? t('dealDetailTab.tradeIn') :
                                                                                dealData.deal_origination === "Service Department" ? t('dealDetailTab.serviceDepartment') : dealData.deal_origination
                                                } : null}
                                                onChange={(option) => handleDealInputChange({ target: { name: 'deal_origination', value: option?.value || '' } })}
                                                options={[
                                                    { value: "Unknown", label: t('dealDetailTab.unknown') },
                                                    { value: "Walk-in", label: t('dealDetailTab.walkIn') },
                                                    { value: "Phone", label: t('dealDetailTab.phone') },
                                                    { value: "Internet", label: t('dealDetailTab.internet') },
                                                    { value: "Referral", label: t('dealDetailTab.referral') },
                                                    { value: "Previous Customer", label: t('dealDetailTab.previousCustomer') },
                                                    { value: "Trade-in", label: t('dealDetailTab.tradeIn') },
                                                    { value: "Service Department", label: t('dealDetailTab.serviceDepartment') }
                                                ]}
                                                isClearable
                                            />
                                        </FormGroup>
                                    </div>
                                </div>

                                <FormGroup>
                                    <Label for="customer_hear_about">{t('dealDetailTab.howDidCustomerHearAbout')}</Label>
                                    <Select
                                        activeColor={activeColor}
                                        styles={selectStyles}
                                        className="react-select"
                                        placeholder={t('dealDetailTab.selectHowDidCustomerHearAbout')}
                                        name="customer_hear_about"
                                        isSearchable={false}
                                        closeMenuOnSelect={true}
                                        value={dealData.customer_hear_about ? {
                                            value: dealData.customer_hear_about,
                                            label: dealData.customer_hear_about === "Unknown" ? t('dealDetailTab.unknown') :
                                                dealData.customer_hear_about === "Advertisement / Referral Channel" ? t('dealDetailTab.advertisementReferralChannel') :
                                                    dealData.customer_hear_about === "Customer" ? t('dealDetailTab.customer') : dealData.customer_hear_about
                                        } : null}
                                        onChange={(option) => handleDealInputChange({ target: { name: 'customer_hear_about', value: option?.value || '' } })}
                                        options={[
                                            { value: "Unknown", label: t('dealDetailTab.unknown') },
                                            { value: "Advertisement / Referral Channel", label: t('dealDetailTab.advertisementReferralChannel') },
                                            { value: "Customer", label: t('dealDetailTab.customer') }
                                        ]}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label for="referralSource">{t('dealDetailTab.referralSource')}</Label>
                                    <Select
                                        activeColor={activeColor}
                                        styles={selectStyles}
                                        className="react-select"
                                        placeholder={t('dealDetailTab.selectReferralSource')}
                                        name="referral_source"
                                        isDisabled={dealData.customer_hear_about !== "Advertisement / Referral Channel"}
                                        isSearchable
                                        closeMenuOnSelect={true}
                                        value={dealData.referral_source ? {
                                            value: dealData.referral_source,
                                            label: dealData.referral_source === "add_new_referral_source"
                                                ? t('dealDetailTab.addNewReferralSource')
                                                : referralSourceOptions.find(source => String(source.id) === String(dealData.referral_source))?.name || dealData.referral_source
                                        } : null}
                                        onChange={(option) => {
                                            if (option?.value === "add_new_referral_source") {
                                                handleReferralSourceChange("add_new_referral_source");
                                            } else {
                                                handleReferralSourceChange(option?.value || "");
                                            }
                                        }}
                                        options={[
                                            { value: '', label: t('dealDetailTab.selectReferralSource') },
                                            { value: "add_new_referral_source", label: t('dealDetailTab.addNewReferralSource') },
                                            ...referralSourceOptions.map(source => ({
                                                value: String(source.id),
                                                label: source.name
                                            }))
                                        ]}
                                        isClearable
                                    />
                                </FormGroup>
                                {data?.desired_vehicle && (
                                    <FormGroup>
                                        <Label for="desiredVehicle">{t('dealDetailTab.desiredVehicle')}</Label>
                                        <div className="p-2 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                                            <span style={{ fontWeight: '600' }}>
                                                {data.desired_vehicle.is_empty || !data.desired_vehicle.inventory
                                                    ? "Any Year Any Make Any Model"
                                                    : `${data.desired_vehicle.inventory?.inventory_vehicle?.year || ''} ${data.desired_vehicle.inventory?.inventory_vehicle?.make || ''} ${data.desired_vehicle.inventory?.inventory_vehicle?.model || ''}`.trim() || '-'
                                                }
                                            </span>
                                        </div>
                                    </FormGroup>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 mb-6 ">
                        <div className="card rounded-0">
                            <div className="card-header font-weight-bold">{t('dealDetailTab.assignmentInformation')}</div>
                            <div className="card-body">
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="sales_person_1">{t('dealDetailTab.salesPerson1')}</Label>
                                            <Select
                                                activeColor={activeColor}
                                                styles={selectStyles}
                                                className="react-select"
                                                placeholder={t('dealDetailTab.notAssigned')}
                                                name="sales_person_1"
                                                isSearchable
                                                closeMenuOnSelect={true}
                                                value={dealData.sales_person_1 ? {
                                                    value: dealData.sales_person_1,
                                                    label: findSalesGroupPersonName(salesGroupPersonResults, dealData.sales_person_1)
                                                } : null}
                                                onChange={(option) => handleDealInputChange({ target: { name: 'sales_person_1', value: option?.value || '' } })}
                                                options={[
                                                    { value: '', label: t('dealDetailTab.notAssigned') },
                                                    ...salesRoleOptions.map(p => ({
                                                        value: String(p.id),
                                                        label: p.name
                                                    }))
                                                ]}
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="sales_person_1_commission">{t('dealDetailTab.salesPerson1Commission')}</Label>
                                            <Input
                                                type="text"
                                                name="sales_person_1_commission"
                                                id="sales_person_1_commission"
                                                value={focusedField === 'sales_person_1_commission' ? dealData.sales_person_1_commission : dealData.sales_person_1_commission + '%'}
                                                onChange={handleDealInputChange}
                                                onFocus={() => handleFocus('sales_person_1_commission')}
                                                onBlur={handleBlur}
                                                className="form-control"
                                                style={inputStyle}
                                                placeholder="0%"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="sales_person_2">{t('dealDetailTab.salesPerson2')}</Label>
                                            <Select
                                                activeColor={activeColor}
                                                styles={selectStyles}
                                                className="react-select"
                                                placeholder={t('dealDetailTab.notAssigned')}
                                                name="sales_person_2"
                                                isSearchable
                                                closeMenuOnSelect={true}
                                                value={dealData.sales_person_2 ? {
                                                    value: dealData.sales_person_2,
                                                    label: findSalesGroupPersonName(salesGroupPersonResults, dealData.sales_person_2)
                                                } : null}
                                                onChange={(option) => handleDealInputChange({ target: { name: 'sales_person_2', value: option?.value || '' } })}
                                                options={[
                                                    { value: '', label: t('dealDetailTab.notAssigned') },
                                                    ...salesRoleOptions.map(p => ({
                                                        value: String(p.id),
                                                        label: p.name
                                                    }))
                                                ]}
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="sales_person_2_commission">{t('dealDetailTab.salesPerson2Commission')}</Label>
                                            <Input
                                                type="text"
                                                name="sales_person_2_commission"
                                                id="sales_person_2_commission"
                                                value={focusedField === 'sales_person_2_commission' ? dealData.sales_person_2_commission : dealData.sales_person_2_commission + '%'}
                                                onChange={handleDealInputChange}
                                                onFocus={() => handleFocus('sales_person_2_commission')}
                                                onBlur={handleBlur}
                                                className="form-control"
                                                style={inputStyle}
                                                placeholder="0%"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label for="sales_manager">{t('dealDetailTab.salesManager')}</Label>
                                            <Select
                                                activeColor={activeColor}
                                                styles={selectStyles}
                                                className="react-select"
                                                placeholder={t('dealDetailTab.notAssigned')}
                                                name="sales_manager"
                                                isSearchable
                                                closeMenuOnSelect={true}
                                                value={dealData.sales_manager ? {
                                                    value: dealData.sales_manager,
                                                    label: findSalesGroupPersonName(salesGroupPersonResults, dealData.sales_manager)
                                                } : null}
                                                onChange={(option) => handleDealInputChange({ target: { name: 'sales_manager', value: option?.value || '' } })}
                                                options={[
                                                    { value: '', label: t('dealDetailTab.notAssigned') },
                                                    ...managerRoleOptions.map(p => ({
                                                        value: String(p.id),
                                                        label: p.name
                                                    }))
                                                ]}
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label for="fi_person">{t('dealDetailTab.fIPerson')}</Label>
                                            <Select
                                                activeColor={activeColor}
                                                styles={selectStyles}
                                                className="react-select"
                                                placeholder={t('dealDetailTab.notAssigned')}
                                                name="fi_person"
                                                isSearchable
                                                closeMenuOnSelect={true}
                                                value={dealData.fi_person ? {
                                                    value: dealData.fi_person,
                                                    label: findSalesGroupPersonName(salesGroupPersonResults, dealData.fi_person)
                                                } : null}
                                                onChange={(option) => handleDealInputChange({ target: { name: 'fi_person', value: option?.value || '' } })}
                                                options={[
                                                    { value: '', label: t('dealDetailTab.notAssigned') },
                                                    ...fiRoleOptions.map(p => ({
                                                        value: String(p.id),
                                                        label: p.name
                                                    }))
                                                ]}
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label for="closing_probability">{t('dealDetailTab.closingProbability')}</Label>
                                            <Input
                                                type="text"
                                                name="closing_probability"
                                                id="closing_probability"
                                                value={focusedField === 'closing_probability' ? dealData.closing_probability : dealData.closing_probability + '%'}
                                                onChange={handleDealInputChange}
                                                onFocus={() => handleFocus('closing_probability')}
                                                onBlur={handleBlur}
                                                className="form-control"
                                                style={inputStyle}
                                                placeholder="50%"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showAddReferralSource && (
                <ChannelsEmbedded
                    activeColor={activeColor}
                    defaultType="lead_referral_source"
                    onClose={() => setShowAddReferralSource(false)}
                    onCreated={(created) => {
                        setDealData(prev => ({ ...prev, referral_source: String(created.id) }));
                        setShowAddReferralSource(false);
                    }}
                />
            )}
        </div>
    );
});

export default DealDetailTab; 
